<script setup>
import { ref, reactive, computed, onMounted, onActivated } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import { useBase, data, hora, CHIP_TIPO } from './base';

import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FiltroSelect from './FiltroSelect.vue';

import SandboxAPI from 'dashboard/api/fandata/sandbox';

// T-186, tela 3a — abrir a Base de conhecimento mostra a HISTÓRIA, não as 334 respostas.
//
// Sobre as cores: o desenho veio em hexadecimal do tema escuro. Aqui eles viram os tokens do
// Chatwoot (n-slate, n-amber, n-alpha), senão a tela ficaria ilegível para quem usa o tema claro.
// A hierarquia e os pesos são os do desenho; só a origem da cor muda.
const { t } = useI18n();
const { irPara } = useBase();

const carregando = ref(true);
const noAr = reactive({
  respostas: 0,
  rotulo: null,
  publicada_em: null,
  publicada_por: null,
});
const rascunho = ref({ aberto: false });
const versoes = ref([]);
const tipos = ref({});
const filtros = reactive({ busca: '', tipo: '' });

const TIPOS = ['publicacao', 'importacao', 'reversao'];

// Tipo é chip colorido, não texto: é o que deixa importação e reversão saltarem numa lista de
// publicações iguais.
// ⚠️ `reset-base` nos <input> e `m-0 border-solid` nos <select>/<textarea> não são enfeite.
// O `_base.scss` estiliza todo campo de formulário por seletor de elemento com uma pilha de
// `:not()`, e a especificidade disso vence qualquer classe do Tailwind: sem o reset, todo input
// desta tela volta a ter largura cheia, margem de 16px embaixo e `border-none` com outline no
// lugar da borda. Era isso que sumia com as bordas e esticava o campo de busca.

// A coluna "Comportamento" saiu: desde que a voz publica em separado, publicação de base nunca
// muda o jeito de falar, e a coluna era um "—" em toda linha. A história da voz tem lista própria.
const GRADE =
  'grid grid-cols-[120px_minmax(280px,1fr)_200px_130px_120px] gap-3 items-center';

const pendentes = computed(() => {
  const r = rascunho.value;
  return (r.alterados || 0) + (r.novos || 0) + (r.a_sair || 0);
});

const quemMexeu = computed(() =>
  (rascunho.value.por_pessoa || [])
    .map(p => t('BASE_INDEX.RASCUNHO.PESSOA', { nome: p.nome, n: p.respostas }))
    .join(' · ')
);

const opcoesTipo = computed(() => [
  { valor: '', rotulo: t('BASE_INDEX.VERSOES.TIPO_TODOS') },
  ...TIPOS.map(tipo => ({
    valor: tipo,
    rotulo: t(`BASE_INDEX.TIPO.${tipo.toUpperCase()}`),
    contagem: tipos.value[tipo],
  })).filter(opcao => opcao.contagem),
]);

const carregar = async () => {
  carregando.value = true;
  try {
    const { data } = await SandboxAPI.linhaDoTempo(filtros);
    Object.assign(noAr, data.no_ar || {});
    rascunho.value = data.rascunho || { aberto: false };
    versoes.value = data.versoes || [];
    tipos.value = data.tipos || {};
  } catch (error) {
    useAlert(t('BASE_INDEX.ERRO'));
  } finally {
    carregando.value = false;
  }
};

const editarBase = () => irPara('capitao_base_editar');
// "Ver o que muda" é a mesma tela de publicar: o diff É a decisão, não uma leitura à parte.
const verOQueMuda = () => irPara('capitao_base_publicar');
const abrirVersao = versao => irPara('capitao_base_versao', { id: versao.id });
// "Onde o Bira erra" saiu daqui (decisão de 15/09): mora só em Relatórios. Uma porta a menos
// para a mesma sala é uma pergunta a menos sobre qual das duas está certa.

// Exportar e importar são a mesma conversa (a planilha que sai é a que volta), então as duas
// moram na tela de importar, e daqui só se vai até lá.
const planilha = () => irPara('capitao_base_importar');
// Leva ao ÍNDICE da voz (a tela Bira), e não ao editor dela: entrar direto no editor pulava a
// lista de versões, que é a primeira coisa que alguém quer ver antes de mexer no jeito de falar.
const comportamento = () => irPara('capitao_calibracao');

// Descartar o rascunho inteiro só existia no sandbox, que saiu. É a saída de quem importou a
// planilha errada ou fez bagunça: volta tudo para o que está no ar, e nada do que já foi
// publicado se perde.
const confirmandoDescarte = ref(false);

const descartar = async () => {
  confirmandoDescarte.value = false;
  try {
    await SandboxAPI.descartar();
    useAlert(t('BASE_INDEX.RASCUNHO.DESCARTADO'));
    await carregar();
  } catch (error) {
    useAlert(t('BASE_INDEX.ERRO'));
  }
};

// As telas de Configurações vivem dentro de um <keep-alive>: sair e voltar NÃO remonta o
// componente, então `onMounted` não roda de novo e a tela volta com o estado de antes. Depois de
// publicar, isso significa uma tela achando que ainda existe um rascunho que já subiu, e todo
// salvamento batendo em 422. Recarregar ao reativar é o que mantém a tela e o servidor falando
// da mesma coisa.
let jaMontou = false;

onActivated(() => {
  if (!jaMontou) {
    jaMontou = true;
    return;
  }
  carregar();
});

onMounted(carregar);
</script>

<template>
  <SettingsLayout
    :is-loading="carregando"
    :loading-message="$t('BASE_INDEX.HEADER')"
  >
    <template #body>
      <div class="flex flex-col gap-3.5 w-full">
        <h1 class="text-[19px] font-semibold text-n-slate-12">
          {{ $t('BASE_INDEX.HEADER') }}
        </h1>

        <!-- No ar agora -->
        <section
          class="rounded-md border border-n-strong bg-n-solid-1 px-[18px] py-4 flex flex-wrap items-center gap-x-4 gap-y-2"
        >
          <div class="flex-1 min-w-[280px]">
            <div class="flex items-center gap-2.5">
              <h2 class="text-[15px] font-semibold text-n-slate-12">
                {{ $t('BASE_INDEX.NO_AR.TITULO') }}
              </h2>
              <span
                class="font-mono text-[10px] tracking-[.06em] px-1.5 py-0.5 rounded-sm bg-n-alpha-2 text-n-slate-11"
              >
                {{ $t('BASE_INDEX.NO_AR.RESPOSTAS', { n: noAr.respostas }) }}
              </span>
            </div>
            <p class="mt-1 text-[12.5px] text-n-slate-11">
              <template v-if="noAr.publicada_em">
                {{
                  $t('BASE_INDEX.NO_AR.PUBLICADA', {
                    data: data(noAr.publicada_em),
                    hora: hora(noAr.publicada_em),
                    autor: noAr.publicada_por || '—',
                  })
                }}
                <template v-if="noAr.rotulo">· “{{ noAr.rotulo }}”</template>
              </template>
              <template v-else>{{
                $t('BASE_INDEX.NO_AR.NUNCA_PUBLICADA')
              }}</template>
            </p>
          </div>
          <div class="flex flex-wrap gap-2">
            <Button
              :label="$t('BASE_INDEX.NO_AR.PLANILHA')"
              variant="faded"
              color="slate"
              size="sm"
              @click="planilha"
            />
            <Button
              :label="$t('BASE_INDEX.NO_AR.COMPORTAMENTO')"
              variant="faded"
              color="slate"
              size="sm"
              @click="comportamento"
            />
            <Button
              :label="$t('BASE_INDEX.NO_AR.EDITAR')"
              color="amber"
              size="sm"
              @click="editarBase"
            />
          </div>
        </section>

        <!-- Rascunho aberto. Sem rascunho, o bloco NÃO EXISTE: é o estado da maioria dos dias. -->
        <section
          v-if="rascunho.aberto && pendentes > 0"
          class="rounded-md border border-n-amber-6 bg-n-amber-2 px-[18px] py-3.5 flex flex-wrap items-center gap-x-4 gap-y-2"
        >
          <div class="flex-1 min-w-[280px]">
            <div class="flex items-center gap-2.5 flex-wrap">
              <span
                class="font-mono text-[10.5px] tracking-[.06em] px-1.5 py-0.5 rounded-sm bg-n-amber-3 text-n-amber-11"
              >
                {{ $t('BASE_INDEX.RASCUNHO.ETIQUETA') }}
              </span>
              <h2 class="text-sm font-semibold text-n-slate-12">
                {{ $t('BASE_INDEX.RASCUNHO.TITULO', { n: pendentes }) }}
              </h2>
            </div>
            <p class="mt-1 text-xs text-n-amber-11">
              {{ quemMexeu }}
              <template v-if="rascunho.ultima_escrita">
                ·
                {{
                  $t('BASE_INDEX.RASCUNHO.ULTIMA', {
                    hora: hora(rascunho.ultima_escrita),
                  })
                }}
              </template>
            </p>
          </div>
          <div class="flex flex-wrap gap-2">
            <Button
              :label="$t('BASE_INDEX.RASCUNHO.VER_MUDANCAS')"
              variant="faded"
              color="amber"
              size="sm"
              @click="verOQueMuda"
            />
            <Button
              :label="$t('BASE_INDEX.RASCUNHO.CONTINUAR')"
              variant="faded"
              color="amber"
              size="sm"
              @click="editarBase"
            />
            <Button
              :label="$t('BASE_INDEX.RASCUNHO.DESCARTAR')"
              variant="link"
              color="slate"
              size="sm"
              @click="confirmandoDescarte = true"
            />
          </div>
        </section>

        <!-- Descartar não pergunta "tem certeza?": diz o que se perde, que é o trabalho da
             colega junto com o seu. -->
        <section
          v-if="confirmandoDescarte"
          class="rounded-md border border-solid border-n-ruby-6 bg-n-ruby-3 px-[18px] py-3.5 flex flex-col gap-2"
        >
          <p class="text-sm font-medium text-n-slate-12">
            {{ $t('BASE_INDEX.RASCUNHO.DESCARTAR_TITULO', { n: pendentes }) }}
          </p>
          <p class="text-[12.5px] text-n-slate-11">
            {{ $t('BASE_INDEX.RASCUNHO.DESCARTAR_CORPO') }}
          </p>
          <div class="flex flex-wrap gap-2 mt-1">
            <Button
              :label="$t('BASE_INDEX.RASCUNHO.DESCARTAR_CANCELAR')"
              variant="faded"
              color="slate"
              size="sm"
              @click="confirmandoDescarte = false"
            />
            <Button
              :label="$t('BASE_INDEX.RASCUNHO.DESCARTAR_CONFIRMAR')"
              color="ruby"
              size="sm"
              @click="descartar"
            />
          </div>
        </section>

        <!-- Versões anteriores -->
        <section class="flex flex-col gap-2.5">
          <div class="flex items-center gap-2.5">
            <h2 class="text-sm font-semibold text-n-slate-12 mr-auto">
              {{ $t('BASE_INDEX.VERSOES.TITULO') }}
            </h2>
            <input
              v-model="filtros.busca"
              type="search"
              class="reset-base w-[260px] h-[34px] rounded-[5px] border border-solid border-n-strong bg-n-solid-2 px-3 text-[13px] text-n-slate-12"
              :placeholder="$t('BASE_INDEX.VERSOES.BUSCA')"
              @keyup.enter="carregar"
            />
            <FiltroSelect
              v-model="filtros.tipo"
              :rotulo="$t('BASE_INDEX.VERSOES.TIPO_ROTULO')"
              :opcoes="opcoesTipo"
              @update:model-value="carregar"
            />
          </div>

          <div v-if="versoes.length">
            <div
              :class="GRADE"
              class="px-3.5 py-2 border-b border-n-weak font-mono text-[10.5px] uppercase tracking-[.06em] text-n-slate-10"
            >
              <span>{{ $t('BASE_INDEX.VERSOES.DATA') }}</span>
              <span>{{ $t('BASE_INDEX.VERSOES.ROTULO') }}</span>
              <span>{{ $t('BASE_INDEX.VERSOES.AUTOR') }}</span>
              <span>{{ $t('BASE_INDEX.VERSOES.TIPO') }}</span>
              <span class="text-right">{{
                $t('BASE_INDEX.VERSOES.RESPOSTAS')
              }}</span>
            </div>
            <button
              v-for="versao in versoes"
              :key="versao.id"
              type="button"
              :class="GRADE"
              class="w-full text-left px-3.5 py-3 border-b border-n-weak hover:bg-n-alpha-1"
              @click="abrirVersao(versao)"
            >
              <span class="text-[13px] text-n-slate-12 whitespace-nowrap">
                {{ data(versao.quando) }}
                <span class="text-xs text-n-slate-10">{{
                  hora(versao.quando)
                }}</span>
              </span>
              <span class="text-[13px] font-medium text-n-slate-12 truncate">
                {{ versao.rotulo }}
              </span>
              <span class="text-[13px] text-n-slate-11 truncate">{{
                versao.autor || '—'
              }}</span>
              <span>
                <span
                  class="font-mono text-[10px] tracking-[.06em] px-1.5 py-0.5 rounded-sm"
                  :class="CHIP_TIPO[versao.tipo] || CHIP_TIPO.publicacao"
                >
                  {{
                    $t(
                      `BASE_INDEX.TIPO.${(versao.tipo || 'publicacao').toUpperCase()}`
                    )
                  }}
                </span>
              </span>
              <span class="font-mono text-xs text-n-slate-11 text-right">
                {{ versao.respostas }}
              </span>
            </button>
          </div>

          <p v-else class="text-sm text-n-slate-11 py-6">
            {{
              filtros.busca || filtros.tipo
                ? $t('BASE_INDEX.VERSOES.SEM_RESULTADO')
                : $t('BASE_INDEX.VERSOES.VAZIO')
            }}
          </p>
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
